import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConditionalFilterPlugin,
  FileSelection,
  GoogleFontIcon,
  InlineAlert,
  jsonEditorWidgets,
  Modal,
  SVGIcon,
  Upload,
} from '@asset-metrix/portal-ui';
import { useEditor, useEditorPlugin, Widget } from '@sagold/react-json-editor';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import { useGetUserFilteredData, useMetadataInputsDisabled } from './hooks';
import { useDocUploadStore } from './store';

import styles from '../docUpload/doc-upload-modal.module.scss';

export function SingleStepUpload() {
  const { t } = useTranslation();
  const {
    acceptedMimeTypes,
    cleanupAndClose,
    tableFilterInstanceId,
    dispatchFileStateAction,
    editorErrors,
    fetchOptions,
    fileState,
    isTableModalValuesLoading,
    jsonSchema,
    modalError,
    onUploadSuccess,
    uploadAllFiles,
    setEditorErrors,
    uploadSingleFile,
    withinPortal,
  } = useDocUploadStore(
    useShallow((state) => ({
      acceptedMimeTypes: state.acceptedMimeTypes,
      cleanupAndClose: state.cleanupAndClose,
      contentId: state.contentId,
      tableFilterInstanceId: state.tableFilterInstanceId,
      dispatchFileStateAction: state.dispatchFileStateAction,
      editorErrors: state.editorErrors,
      fetchOptions: state.fetchOptions,
      fileState: state.fileState,
      isTableModalValuesLoading: state.isTableModalValuesLoading,
      jsonSchema: state.jsonSchema,
      modalError: state.modalError,
      onUploadSuccess: state.onUploadSuccess,
      uploadAllFiles: state.uploadAllFiles,
      setEditorErrors: state.setEditorErrors,
      uploadSingleFile: state.uploadSingleFile,
      withinPortal: state.withinPortal,
    }))
  );

  const initialData = useGetUserFilteredData(jsonSchema, tableFilterInstanceId);
  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: jsonSchema,
    onChange: (_data, _root, editor) => {
      setEditorErrors(editor.getErrors());
    },
    validate: true,
    data: initialData,
  });
  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions,
  });

  // function used by <Upload /> component to retry single file upload if it fails.
  const uploadFile = useCallback(
    async (fileSelection: FileSelection) => {
      const uploaded = await uploadSingleFile(fileSelection, editor?.getData());
      // only trigger onUploadSuccess if file is uploaded successfully.
      if (uploaded) {
        await onUploadSuccess?.();
      }
    },
    [editor, uploadSingleFile, onUploadSuccess]
  );

  const metadataInputsDisabled = useMetadataInputsDisabled();

  const isFileStateInitial = useMemo(
    () => fileState.every((selection) => selection.uploadState === 'initial'),
    [fileState]
  );

  const isFileStatePending = useMemo(
    () => fileState.some((selection) => selection.uploadState === 'isPending'),
    [fileState]
  );

  const isUploadDisabled = useMemo(
    () =>
      editorErrors.length > 0 ||
      fileState.length === 0 ||
      Boolean(isTableModalValuesLoading) ||
      !!modalError,
    [
      editorErrors.length,
      fileState.length,
      isTableModalValuesLoading,
      modalError,
    ]
  );

  const PrimaryButton = useMemo(() => {
    // files have uploaded
    if (!isFileStateInitial) {
      return (
        <Button
          icon={
            isFileStatePending ? (
              <SVGIcon name="upload" />
            ) : (
              <GoogleFontIcon name="check" />
            )
          }
          onClick={cleanupAndClose}
          disabled={isFileStatePending}
        >
          {t(isFileStatePending ? 'app.upload.uploading' : 'app.upload.okay')}
        </Button>
      );
    }

    return (
      <Button
        icon={<SVGIcon name="upload" />}
        onClick={() => uploadAllFiles(editor?.getData())}
        disabled={isUploadDisabled}
      >
        {t('app.upload.upload')}
      </Button>
    );
  }, [
    cleanupAndClose,
    editor,
    isFileStateInitial,
    isFileStatePending,
    isUploadDisabled,
    uploadAllFiles,
    t,
  ]);

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={cleanupAndClose}
            disabled={!isFileStateInitial}
          >
            {t('app.upload.cancel')}
          </Button>
          {PrimaryButton}
        </>
      }
      contentClassName={styles['main-content']}
      modalType="action"
      opened={true}
      onClose={cleanupAndClose}
      size={960}
      title={t('app.upload.zipUploadModal.headline')}
      withinPortal={withinPortal}
    >
      {modalError && (
        <InlineAlert className={styles['alert']}>{modalError}</InlineAlert>
      )}
      <div className={styles['content']}>
        <Upload
          accept={acceptedMimeTypes}
          dispatch={dispatchFileStateAction}
          fileState={fileState}
          uploadFile={uploadFile}
        />
        <div
          className={clsx(
            'portal-form rje-form rje-theme rje-theme--light',
            styles['editor']
          )}
        >
          <Widget
            editor={editor}
            options={{
              disabled: metadataInputsDisabled,
              loading: Boolean(isTableModalValuesLoading),
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
